import { AppSpec } from '../types';

/**
 * Pure spec-validation helpers — kept free of React/Amplify imports
 * so they can be unit-tested in isolation.
 */

/** Subdomains already in use by core platform apps — never allow overwrite. */
export const RESERVED_SUBDOMAINS = [
  'eamatrix', 'ea-qa', 'ea-assessment', 'ea-roadmap', 'ea-specdriven',
  'ea-notebookllm', 'ea-aiskills', 'ea-tm', 'ea-guide', 'ea-mainframe',
  'bucket', 'supporthub', 'aitrans', 'dev', 'ea', 'ea-chat-api',
  'ea-open', 'ppt-maker', 'shimizu', 'support', 'www', 'api', 'mail',
];

export function sanitizeSubdomain(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40);
}

export function sanitizeDbName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 60);
}

export interface ValidationIssue {
  field: string;
  message: string;
}

export function validateSubdomain(
  subdomain: string,
  existingSubdomains: string[] = []
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!subdomain) {
    issues.push({ field: 'subdomain', message: 'Subdomain is required' });
    return issues;
  }
  if (subdomain.length < 2 || subdomain.length > 40) {
    issues.push({ field: 'subdomain', message: 'Subdomain must be 2–40 characters' });
  }
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(subdomain)) {
    issues.push({
      field: 'subdomain',
      message: 'Use lowercase letters, numbers and hyphens; must start and end with a letter or number',
    });
  }
  if (RESERVED_SUBDOMAINS.includes(subdomain)) {
    issues.push({ field: 'subdomain', message: `"${subdomain}" is reserved by a platform app` });
  }
  if (existingSubdomains.includes(subdomain)) {
    issues.push({ field: 'subdomain', message: `An app with subdomain "${subdomain}" already exists` });
  }
  return issues;
}

/**
 * Full spec validation. `existingSubdomains` should exclude the spec
 * being edited (so redeploys of the same app pass).
 */
export function validateSpec(
  spec: AppSpec,
  existingSubdomains: string[] = []
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  issues.push(...validateSubdomain(spec.subdomain, existingSubdomains));

  if (!spec.displayName || !spec.displayName.trim()) {
    issues.push({ field: 'displayName', message: 'Display name is required' });
  } else if (spec.displayName.trim().length < 2) {
    issues.push({ field: 'displayName', message: 'Display name must be at least 2 characters' });
  }

  if (!spec.description || !spec.description.trim()) {
    issues.push({ field: 'description', message: 'Description is required' });
  } else if (spec.description.trim().length < 10) {
    issues.push({
      field: 'description',
      message: 'Description should be at least 10 characters — the AI uses it to generate your app',
    });
  }

  if (spec.skills.length === 0) {
    issues.push({ field: 'skills', message: 'Select at least one skill' });
  }

  if (spec.requirements.length === 0) {
    issues.push({ field: 'requirements', message: 'Define at least one requirement' });
  } else if (!spec.requirements.some(r => r.priority === 'must' || r.priority === 'should')) {
    issues.push({
      field: 'requirements',
      message: 'Add at least one Must-have or Should-have requirement',
    });
  }

  const s = spec.steering;
  if (s.dataStorage === 'mysql') {
    if (!s.mysqlDbName) {
      issues.push({ field: 'steering', message: 'MySQL database name is required for MySQL storage' });
    } else if (!/^[a-z][a-z0-9_]{1,59}$/.test(s.mysqlDbName)) {
      issues.push({
        field: 'steering',
        message: 'Database name must start with a letter and use only lowercase letters, numbers, underscores',
      });
    }
  }

  if (s.authRequired && s.allowedGroups.length === 0) {
    issues.push({ field: 'steering', message: 'Select at least one allowed group when authentication is required' });
  }

  if (s.hasBackend && (!s.apiPrefix || !s.apiPrefix.startsWith('/'))) {
    issues.push({ field: 'steering', message: 'API prefix must start with "/" (e.g. /api)' });
  }

  return issues;
}

/** Map a wizard step key to a short human label. */
export const WIZARD_STEP_LABELS: Record<string, string> = {
  register_app: 'Register app',
  allocate_port: 'Allocate port',
  scaffold_app: 'Scaffold project',
  provision_s3: 'Provision S3',
  provision_cloudfront: 'Provision CloudFront',
  provision_cognito: 'Create auth client',
  provision_database: 'Create database',
  provision_dns: 'Configure DNS',
  security_check: 'Security check',
  sdk_codegen: 'Generate app code (AI)',
  deploy_frontend: 'Deploy frontend',
  sdk_docs: 'Generate documentation',
  sdk_videos: 'Generate demo videos',
  github_push: 'Push to GitHub',
};
